'use client'

/**
 * One-click "Begin course" button for the enroll card's 'enroll' mode (a
 * member who has access but hasn't enrolled yet). Enrolls via the
 * enrollInCourse server action, then navigates straight to the first
 * lesson (`beginHref`) — the point of "Begin course" is to start the
 * course, not to re-render the card into its enrolled state and make the
 * learner click a second time. (Visiting the item also auto-enrolls, so
 * the explicit enroll here just guarantees the row exists first.)
 */
import { useState, useTransition } from 'react'
import { useTranslations } from 'next-intl'
import { useRouter } from '@/i18n/navigation'
import { enrollInCourse } from '@/app/[locale]/(public)/courses/[slug]/enroll-actions'

export function EnrollActionButton({
  courseSanityId,
  beginHref,
  label,
}: {
  courseSanityId: string
  /** First-lesson deep link — where "Begin course" lands the learner. */
  beginHref: string
  label?: string
}) {
  const router = useRouter()
  const t = useTranslations('courseDetail.enroll')
  const [pending, startTransition] = useTransition()
  const [error, setError] = useState<string | null>(null)

  const onClick = () => {
    setError(null)
    startTransition(async () => {
      const res = await enrollInCourse(courseSanityId)
      if (!res.ok) {
        setError(
          res.reason === 'unauthenticated' ? t('signInError') : t('enrollError'),
        )
        return
      }
      router.push(beginHref)
    })
  }

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        disabled={pending}
        className="mt-4 flex w-full items-center justify-center rounded-[12px] bg-primary px-4 py-[15px] text-[15px] font-bold text-white transition-opacity hover:opacity-90 active:opacity-70 disabled:opacity-60"
      >
        {pending ? t('beginning') : (label ?? t('beginCourse'))}
      </button>
      {error && <p className="mt-2 text-center text-xs text-destructive">{error}</p>}
    </>
  )
}
